// Google Ads click identifiers for conversion attribution.
//
// This is the Google pendant to facebookTracking.ts. Google Ads attributes a
// conversion via the `gclid` (or `gbraid`/`wbraid` on iOS / consent-mode) that
// it appends to the landing URL. gtag's own auto-tagging sets `_gcl_*` cookies,
// but ONLY after marketing consent — cold ad traffic that declines consent
// leaves no gclid trail, and there is then nothing to import as an offline
// conversion later.
//
// So we capture the `gclid` URL param ourselves — aber CONSENT-GATED (TTDSG,
// Entscheidung 21.07.2026 vor dem Scharfschalten der Offline-Uploads):
// Beim Landen wird der Wert nur IN-MEMORY gestasht; das Cookie (90 Tage,
// first-touch) wird erst geschrieben, wenn Marketing-Consent vorliegt —
// entweder sofort (Consent schon gespeichert) oder beim Klick im Banner
// (useCookieConsent.saveConsent → persistGclid). Lehnt der Besucher ab,
// verlässt der Klick-Identifier nie den Arbeitsspeicher.

const GCLID_COOKIE = "cc_gclid"; // our own cookie; do not clobber gtag's _gcl_aw
const GBRAID_COOKIE = "cc_gbraid";
const WBRAID_COOKIE = "cc_wbraid";
const NINETY_DAYS_SECONDS = 60 * 60 * 24 * 90; // Google gclid attribution window

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
}

// Google click-id params, in priority order. gbraid/wbraid appear instead of
// gclid on iOS / restricted-tracking traffic.
const CLICK_IDS = [
  { param: "gclid", cookie: GCLID_COOKIE },
  { param: "gbraid", cookie: GBRAID_COOKIE },
  { param: "wbraid", cookie: WBRAID_COOKIE },
] as const;

// In-Memory-Stash der Landing-Klick-IDs (SPA: überlebt Navigationen ohne Reload)
const stashedClickIds: Partial<Record<"gclid" | "gbraid" | "wbraid", string>> = {};

/**
 * Capture Google click identifiers from the current landing URL.
 * Stash ist immer in-memory; Cookies werden nur bei Marketing-Consent
 * geschrieben (sonst später via persistGclid() nach Banner-Opt-in).
 * Idempotent, never throws — tracking must not break page load.
 */
export function captureGclid(hasMarketingConsent: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const { param } of CLICK_IDS) {
      const value = params.get(param);
      if (value && !stashedClickIds[param]) stashedClickIds[param] = value;
    }
    if (hasMarketingConsent) persistGclid();
  } catch {
    // Never break page load over tracking.
  }
}

/**
 * Persistiert gestashte Klick-IDs als First-Touch-Cookies. Wird aufgerufen,
 * sobald Marketing-Consent vorliegt (Load mit gespeichertem Consent oder
 * Banner-Opt-in in derselben Session).
 */
export function persistGclid(): void {
  try {
    for (const { param, cookie } of CLICK_IDS) {
      const value = stashedClickIds[param];
      if (!value) continue;
      if (readCookie(cookie)) continue; // first-touch: keep the original
      writeCookie(cookie, value, NINETY_DAYS_SECONDS);
    }
  } catch {
    // Never break over tracking.
  }
}

/**
 * Read the stored Google click identifiers for conversion forwarding.
 * Returns only the fields that are actually present.
 */
export function getGoogleTrackingData(): {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
} {
  const data: { gclid?: string; gbraid?: string; wbraid?: string } = {};
  const gclid = readCookie(GCLID_COOKIE);
  const gbraid = readCookie(GBRAID_COOKIE);
  const wbraid = readCookie(WBRAID_COOKIE);
  if (gclid) data.gclid = gclid;
  if (gbraid) data.gbraid = gbraid;
  if (wbraid) data.wbraid = wbraid;
  return data;
}
